import { CreateLeaveRequestDto, LeaveRequestDto } from '../commonTypes/leaveRequest.types';
import { Page } from '../commonTypes/pageable.types';
import { diContainer } from '../libs/container';
import { ApiBaseClass, IServiceHttp } from './ApiBase';

export namespace LeaveRequestApiRequest {
    export interface LeaveRequestsForUser {
        query: {
            page?: number;
            size?: number;
            sort?: string;
            direction?: string;
        };
    }

    export interface LeaveRequestForUserProfile {
        query: {
            page?: number;
            size?: number;
            sort?: string;
            direction?: string;
        };
        params: {
            userSub: string;
        };
    }

    export interface LeaveRequestsFromOrganizationByInterval {
        query: {
            organizationId: string;
            fromDate: string;
            toDate: string;
            status?: string;
        };
    }

    export interface SubmitLeaveRequest {
        body: CreateLeaveRequestDto;
    }

    export interface Approve {
        params: {
            id: string;
        };
    }

    export interface Reject {
        params: {
            id: string;
        };
        body: {
            rejectReason?: string;
        };
    }

    export interface DeleteLeaveRequest {
        params: {
            id: string;
        };
    }

    export interface GetAbsentColleaguesCount {
        query: {
            from: string;
            to: string;
        };
    }

    export interface EditOngoingLeaveRequest {
        params: { id: string };
        body: { newToDate: string };
    }

    export interface GetLeaveRequestsByUserSub {
        params: { userSub: string };
    }

    export interface LeaveRequestForUserProfileForExport {
        params: {
            userSub: string;
        };
    }
}

export namespace LeaveRequestApiResponse {
    export interface LeaveRequests {
        body: Page<LeaveRequestDto>;
    }

    export interface LeaveRequestData {
        body: LeaveRequestDto;
    }

    export interface LeaveRequestsNonPagable {
        body: LeaveRequestDto[];
    }

    export interface GetAbsentColleaguesCount {
        body: {
            absentTodayCount: number;
            absentPeriodCount: number;
        };
    }
}

export class LeaveRequestApi {
    constructor(private readonly apiBase: ApiBaseClass) {}

    public submitLeaveRequest(rest: IServiceHttp<LeaveRequestApiRequest.SubmitLeaveRequest, LeaveRequestApiResponse.LeaveRequestData>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/leave-request',
        });
    }

    public requestsForUser(rest: IServiceHttp<LeaveRequestApiRequest.LeaveRequestsForUser, LeaveRequestApiResponse.LeaveRequests>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/leave-request',
        });
    }

    public allRequestsFromOrganizationByInterval(
        rest: IServiceHttp<LeaveRequestApiRequest.LeaveRequestsFromOrganizationByInterval, LeaveRequestApiResponse.LeaveRequestsNonPagable>
    ) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/leave-request/organization/interval',
        });
    }

    public approve(rest: IServiceHttp<LeaveRequestApiRequest.Approve, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/leave-request/id/:id/-approve',
        });
    }

    public reject(rest: IServiceHttp<LeaveRequestApiRequest.Reject, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/leave-request/id/:id/-reject',
        });
    }

    public deleteLeaveRequest(rest: IServiceHttp<LeaveRequestApiRequest.DeleteLeaveRequest, LeaveRequestApiResponse.LeaveRequestData>) {
        return this.apiBase.configure({
            ...rest,
            method: 'DELETE',
            url: 'usr/v1/leave-request/id/:id',
        });
    }

    public getOrganizationMembersCount(
        rest: IServiceHttp<LeaveRequestApiRequest.GetAbsentColleaguesCount, LeaveRequestApiResponse.GetAbsentColleaguesCount>
    ) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/leave-request/absent-colleagues-count',
        });
    }

    public editOngoingLeaveRequest(rest: IServiceHttp<LeaveRequestApiRequest.EditOngoingLeaveRequest, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/leave-request/id/:id/-updateOngoing',
        });
    }

    public getLeaveRequestsHistoryForUserProfile(
        rest: IServiceHttp<LeaveRequestApiRequest.LeaveRequestForUserProfile, LeaveRequestApiResponse.LeaveRequests>
    ) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/leave-request/userSub/:userSub/history',
        });
    }

    public getUpcomingLeaveRequestsForUserProfile(
        rest: IServiceHttp<LeaveRequestApiRequest.GetLeaveRequestsByUserSub, LeaveRequestApiResponse.LeaveRequestsNonPagable>
    ) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/leave-request/userSub/:userSub/upcoming',
        });
    }

    public getLeaveRequestsHistoryForUserProfileForExport(
        rest: IServiceHttp<LeaveRequestApiRequest.LeaveRequestForUserProfileForExport, LeaveRequestApiResponse.LeaveRequestData>
    ) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/leave-request/userSub/:userSub/history-for-export',
        });
    }
}

diContainer.bindAsDynamic(LeaveRequestApi, c => new LeaveRequestApi(c.get(ApiBaseClass)));
